import {PortalStore} from "../../types/PortalStore";
import StoreAction from "../../types/StoreAction";
import {ActionTypes} from "../actions/ActionTypes";
import {initialSideBar} from "../../types/SideBarData";
import {TSMap} from "typescript-map";
import produce from "immer";
import {AssetType} from "../../types/AssetType";
import {CheckListStatus} from "../../types/CheckList";
import {AssetState} from "../../types/AssetState";
import {defaultUIStatus} from "../../types/UIStatus";
import {ImageDeleteData} from "../../types/ImageDeleteData";
import {LoggedInUserType} from "../../types/LoggedInUserType";
import InspectionElement from "../../types/InspectionElement";
import { DocumentReuseStatus } from "../../types/Inspection";

const initialState: PortalStore = {
    user: undefined,
    userType: undefined,
    inspections: undefined,
    sideBarData: initialSideBar,
    activeInspection: undefined,
    inspectionDetails: new TSMap(),
    uploadList: new TSMap(),
    activeCheckList: new TSMap(),
    uiStatus: defaultUIStatus,
    globalExpiryDate: "",
    activeExpiryDates: new TSMap<number, string>()
};

const updateObject = (oldObject: any, updatedValues: any) => {
    return {
        ...oldObject,
        ...updatedValues
    };
};

const updateSection = (
    newObject: any,
    existingSections: Array<InspectionElement>,
    sectionId: string
): Array<InspectionElement> => (
    existingSections.map((section) => (
        section.Id.toString() === sectionId ? {
            ...section,
            ...newObject
        } : {
            ...section
        }
    ))
);

const portalReducer = (state = initialState, action: StoreAction) => {
    let userType = undefined;

    switch (action.type) {
        case ActionTypes.UPDATE_UI_STATUS:
            return updateObject(state, {
                uiStatus: action.uiStatus
            });

        case ActionTypes.UPDATE_CHECKLIST:
            for (const checklist of action.checkLists) {
                state.activeCheckList.set(checklist.id, checklist);
            }
            return updateObject(state, {
                activeCheckList: state.activeCheckList
            });
        case ActionTypes.SET_USER_TYPE:
            return updateObject(state, { userType: action.userType })
        case ActionTypes.SET_USER:
            if (action.user && action.user.signInUserSession) {
                let userGroups = action.user.signInUserSession.accessToken.payload["cognito:groups"];

                if (!userGroups) {
                    // If there is no user group for the cognito account, then they must be a document collector,
                    // as surveyors and admin accounts will always have a user group. Set it here in this case.
                    userGroups = [process.env.REACT_APP_DOCUMENT_COLLECTOR_GROUP!];
                }

                if (userGroups.includes(process.env.REACT_APP_DOCUMENT_COLLECTOR_GROUP!)) {
                    userType = LoggedInUserType.DOCUMENT_COLLECTOR;
                } else if (userGroups.includes(process.env.REACT_APP_IDWAL_ADMINISTRATOR_GROUP!)) {
                    userType = LoggedInUserType.IDWAL_ADMIN;
                } else if (userGroups.includes(process.env.REACT_APP_IDWAL_SURVEYOR_GROUP!)) {
                    userType = LoggedInUserType.IDWAL_SURVEYOR;
                }
            }
            return updateObject(state, { user: action.user, userType: userType });
        case ActionTypes.GET_INSPECTIONS:
            return updateObject(state, { inspections: action.inspections });
        case ActionTypes.SET_SIDEBAR_DATA:
            return updateObject(state, { sideBarData: action.sideBarData });

        case ActionTypes.GET_INSPECTION_BY_NUMBER: {
            const inspectionWithDocumentExpiry = {
                ...action.activeInspection,
                sections: action.activeInspection.sections.map((section) => {
                    return {
                        ...section,
                        expiryDate: section.Documents[0]?.metadata['reusable-expiry'] ?? null
                    }
                })
            }
            return updateObject(state, {
                activeInspection: inspectionWithDocumentExpiry,
                inspectionDetails: state.inspectionDetails.set(
                    action.activeInspection.Id,
                    inspectionWithDocumentExpiry
                )
            });}

        case ActionTypes.UPLOAD_ASSET: {
            const inspection = state.activeInspection;

            if (!inspection) {
                return state;
            }

            const assetUploadType = action.assetInfo.assets[0].type;

            const checkList = state.activeCheckList.get(
                action.assetInfo.elementId
            );

            if (assetUploadType === AssetType.CheckList) {
                checkList.assets = checkList.assets.concat(
                    action.assetInfo.assets[0]
                );
            }

            for (let i = 0; i !== inspection.sections.length; i++) {
                if (inspection.sections[i].Id === action.assetInfo.elementId) {
                    if (assetUploadType === AssetType.Image) {
                        inspection.sections[i].Images = inspection.sections[
                            i
                        ].Images.concat(action.assetInfo.assets);
                    }

                    if (assetUploadType === AssetType.Document) {
                        inspection.sections[i].Documents = inspection.sections[
                            i
                        ].Documents.concat(action.assetInfo.assets);
                    }
                }
            }

            inspection.localUpdateTime = Date.now().toString();
            action.assetInfo.assets.forEach((asset: any) => {
                state.uploadList.set(asset.id, { ...asset });
            });

            return {
                ...state,
                ...produce(state, (updatedState) => {
                    updatedState.activeCheckList =
                        updatedState.activeCheckList.set(
                            action.assetInfo.elementId,
                            checkList
                        );
                    updatedState.activeInspection = inspection;
                    updatedState.inspectionDetails.set(
                        inspection?.Id || "",
                        inspection
                    );
                })
            };
        }

        case ActionTypes.UPDATE_ASSET: {
            const inspection = state.activeInspection;
            if (!inspection) {
                return state;
            }

            if (action.assetInfo.asset.mainShip) {
                inspection.MainShipImage = action.assetInfo.asset.url;
            }

            const updatedInspections = state.inspections;

            if (
                action.assetInfo.asset.progress === 100 &&
                action.assetInfo.asset.state === AssetState.DISPLAYING
            ) {
                const index = state.inspections?.findIndex(
                    (item) => item.Id === state.activeInspection?.Id
                );
                if (index !== undefined) {
                    if (updatedInspections) {
                        switch (action.assetInfo.asset.type) {
                            case AssetType.CheckList:
                                updatedInspections[index].NumberOfChecklists++;
                                break;
                            case AssetType.Document:
                                updatedInspections[index].NumberOfDocuments++;
                                break;
                            case AssetType.Image:
                                updatedInspections[index].NumberOfPhotos++;

                                if (action.assetInfo.asset.mainShip) {
                                    updatedInspections[index].MainShipImage =
                                        action.assetInfo.asset.url;
                                }

                                break;
                        }
                    }
                }
            }

            if (action.assetInfo.asset.type === AssetType.CheckList) {
                const checkList = state.activeCheckList.get(
                    action.assetInfo.elementId
                );
                checkList.assets = [action.assetInfo.asset];

                if (
                    checkList.assets[0].progress === 100 &&
                    checkList.assets[0].state === AssetState.DISPLAYING
                ) {
                    checkList.checkListStatus = CheckListStatus.UPLOADED;
                }

                return updateObject(state, {
                    inspections: updatedInspections,
                    activeCheckList: state.activeCheckList.set(
                        action.assetInfo.elementId,
                        checkList
                    )
                });
            }

            const isUploadImages =
                action.assetInfo.asset.type === AssetType.Image;

            for (let i = 0; i !== inspection.sections.length; i++) {
                if (inspection.sections[i].Id === action.assetInfo.elementId) {
                    if (isUploadImages) {
                        inspection.sections[i].Images.map(
                            (obj: any) => action.assetInfo.asset.id === obj.id
                        );
                    } else {
                        inspection.sections[i].Documents.map(
                            (obj: any) => action.assetInfo.asset.id === obj.id
                        );
                    }
                }
            }

            return {
                ...state,
                ...produce(state, (updatedState) => {
                    updatedState.inspections = updatedInspections;
                    updatedState.activeInspection = inspection;
                    updatedState.inspectionDetails.set(
                        inspection?.Id || "",
                        inspection
                    );
                    updatedState.uploadList.set(action.assetInfo.asset.id, {
                        ...action.assetInfo.asset
                    });
                })
            };
        }
        case ActionTypes.DELETE_IMAGE: {
            const inspection = state.activeInspection;
            if (!inspection) {
                return state;
            }

            const sectionIndex = inspection.sections.findIndex(
                (element) =>
                    element.Id.toString() === action.asset.elementId.toString()
            );
            const assetIndex = inspection.sections[
                sectionIndex
            ].Images.findIndex((asset) => asset.id === action.asset.assetId);
            inspection.sections[sectionIndex].Images.splice(assetIndex, 1);

            const index = state.inspections?.findIndex(
                (item) => item.Id === state.activeInspection?.Id
            );

            if (action.asset.mainShip) {
                inspection.MainShipImage = "";
            }

            if (index !== undefined) {
                const updatedInspections = state.inspections;
                if (updatedInspections) {
                    updatedInspections[index].NumberOfPhotos--;

                    if (action.asset.mainShip) {
                        updatedInspections[index].MainShipImage = "";
                    }

                    return updateObject(state, {
                        inspections: updatedInspections,
                        activeInspection: inspection,
                        inspectionDetails: state.inspectionDetails.set(
                            inspection.Id,
                            inspection
                        )
                    });
                }
            }

            return updateObject(state, {
                activeInspection: inspection,
                inspectionDetails: state.inspectionDetails.set(
                    inspection.Id,
                    inspection
                )
            });
        }
        case ActionTypes.DELETE_DOCUMENT: {
            const inspection = state.activeInspection;
            if (!inspection) {
                return state;
            }

            const sectionIndex = inspection.sections.findIndex(
                (element) => element.Id === action.asset.elementId
            );
            const assetIndex = inspection.sections[
                sectionIndex
            ].Documents.findIndex((asset) => asset.id === action.asset.assetId);
            inspection.sections[sectionIndex].Documents.splice(assetIndex, 1);

            const index = state.inspections?.findIndex(
                (item) => item.Id === state.activeInspection?.Id
            );
            if (index !== undefined) {
                const updatedInspections = state.inspections;
                if (updatedInspections) {
                    updatedInspections[index].NumberOfDocuments--;
                    return updateObject(state, {
                        inspections: updatedInspections,
                        activeInspection: inspection,
                        inspectionDetails: state.inspectionDetails.set(
                            inspection.Id,
                            inspection
                        )
                    });
                }
            }

            return updateObject(state, {
                activeInspection: inspection,
                inspectionDetails: state.inspectionDetails.set(
                    inspection.Id,
                    inspection
                )
            });
        }
        case ActionTypes.DELETE_CHECKLIST: {
            const inspection = state.activeInspection;
            if (!inspection) {
                return state;
            }

            const checkList = state.activeCheckList.get(action.asset.elementId);
            checkList.assets = checkList.assets.filter((document: any) => {
                return document.id !== action.asset.assetId;
            });

            if (checkList.assets.length === 0) {
                checkList.checkListStatus = CheckListStatus.NOT_UPLOADED;
            }

            const index = state.inspections?.findIndex(
                (item) => item.Id === state.activeInspection?.Id
            );
            if (index !== undefined) {
                const updatedInspections = state.inspections;
                if (updatedInspections) {
                    updatedInspections[index].NumberOfChecklists--;
                    return updateObject(state, {
                        inspections: updatedInspections,
                        activeCheckList: state.activeCheckList.set(
                            action.asset.elementId,
                            checkList
                        )
                    });
                }
            }

            return updateObject(state, {
                activeCheckList: state.activeCheckList.set(
                    action.asset.elementId,
                    checkList
                )
            });
        }

        case ActionTypes.UPDATE_COMPLETION_STATUS: {
            const inspection = state.activeInspection;
            if (!inspection) {
                return state;
            }

            inspection.SurveyorCompletionStatus = Object.assign(
                {},
                action.surveyorCompletionStatus
            );
            inspection.SurveyorCompletionStatus.InspectionId = inspection.Id;

            const updatedInspections = state.inspections;

            const index = state.inspections?.findIndex(
                (item) => item.Id === state.activeInspection?.Id
            );
            if (index !== undefined) {
                if (updatedInspections) {
                    updatedInspections[index].SurveyorCompletionStatus =
                        inspection.SurveyorCompletionStatus;
                }
            }

            return updateObject(state, {
                activeInspection: inspection,
                inspections: updatedInspections
            });
        }

        case ActionTypes.ADD_COMMENT: {
            const inspection = state.activeInspection;

            if (!inspection) {
                return state;
            }

            const sectionIndex = inspection.sections.findIndex(
                (element) => element.Id === action.asset.elementId
            );

            const section = inspection.sections[sectionIndex];
            const assetUploadType = action.asset.assetType;

            inspection.sections.forEach((element) => {
                if (element.Id === section.Id) {
                    switch (assetUploadType) {
                        case AssetType.Document:
                            element.Documents.forEach((doc) => {
                                if (doc.id === action.asset.assetId) {
                                    doc.metadata.comments =
                                        action.asset.metadata.comments;
                                }
                            });
                            break;
                        default:
                            element.Images.forEach((doc) => {
                                if (doc.id === action.asset.assetId) {
                                    doc.metadata.comments =
                                        action.asset.metadata.comments;
                                }
                            });
                            break;
                    }
                }
            });

            inspection.localUpdateTime = Date.now().toString();

            return updateObject(state, {
                activeInspection: inspection
            });
        }

        case ActionTypes.SET_IMAGE_DELETE_MODE: {
            const inspection = state.activeInspection;

            if (!inspection) {
                return state;
            }

            inspection.ImageDeleteMode = true;

            return updateObject(state, {
                activeInspection: inspection
            });
        }

        case ActionTypes.ADD_IMAGE_TO_DELETE_LIST: {
            const inspection = state.activeInspection;

            if (!inspection) {
                return state;
            }

            if (inspection.ImageDeleteList === undefined) {
                inspection.ImageDeleteList = [];
            }

            const newList: ImageDeleteData[] = Array.from(
                inspection.ImageDeleteList
            );

            if (
                !inspection.ImageDeleteList.find(
                    (image) => action.imageData.assetId === image.assetId
                )
            ) {
                newList.push(action.imageData);
            }

            inspection.ImageDeleteList = newList;

            return updateObject(state, {
                activeInspection: inspection
            });
        }

        case ActionTypes.REMOVE_IMAGE_FROM_DELETE_LIST: {
            const inspection = state.activeInspection;

            if (!inspection || inspection.ImageDeleteList === undefined) {
                return state;
            }

            let newList: ImageDeleteData[] = [];

            newList = inspection.ImageDeleteList.filter((element) => {
                return element.assetId !== action.imageData.assetId;
            });

            inspection.ImageDeleteList = newList;

            return updateObject(state, {
                activeInspection: inspection
            });
        }

        case ActionTypes.CLEAR_DELETE_DATA: {
            const inspection = state.activeInspection;

            if (!inspection || inspection.ImageDeleteList === undefined) {
                return state;
            }

            const newList: ImageDeleteData[] = [];

            inspection.ImageDeleteList = newList;

            return updateObject(state, {
                activeInspection: inspection
            });
        }

        case ActionTypes.UPDATE_INSPECTION_REUSE_STATUS_SUCCESS: {
            const inspection = state.activeInspection;

            if (!inspection) {
                return state;
            }

            inspection.DocumentReuseStatus = action.inspectionReuseStatus;

            // NOT_RE_USE blanks out the documents -- still in reuse mode.
            if (inspection.DocumentReuseStatus === DocumentReuseStatus.NOT_RE_USE) {
                inspection.sections = inspection.sections.map((section) => {
                    if (section.ReUseDocuments) {
                        section.ReUseDocuments = true;
                        section.Documents = [];
                    }

                    return section;
                });
            }

            return updateObject(state, {
                activeInspection: inspection
            });
        }

        case ActionTypes.UPDATE_INSPECTION_REPORT_REUSE_SUCCESS: {
            const inspection = state.activeInspection;

            if (!inspection) {
                return state;
            }

            inspection.DocumentReuseStatus = action.inspectionReuseStatus;
            inspection.sections = inspection.sections.map((section) => {
                const updatedSection = action.updatedReuseElements.find(
                    (element) => element.Id === section.Id
                );

                if (!updatedSection) {
                    return section;
                }

                if (!updatedSection.ValidForReuse) {
                    section.ReUseDocuments = false;
                    section.Documents = [];
                }

                return section;
            });

            return updateObject(state, {
                activeInspection: inspection
            });
        }

        case ActionTypes.ASSIGN_IMAGES_TO_DEFECT_SAVING: {
            // Handle loading state if needed
            return state;
        }

        case ActionTypes.ASSIGN_IMAGES_TO_DEFECT_SUCCESS: {
            const inspection = state.activeInspection;

            if (!inspection) {
                return state;
            }

            // Update the defect with the new images
            const updatedDefects = inspection.defects.map((defect) => {
                if (defect.defectId === action.defect.defectId) {
                    return {
                        ...defect,
                        images: JSON.stringify(action.selectedImages)
                    };
                }
                return defect;
            });

            return updateObject(state, {
                activeInspection: {
                    ...inspection,
                    defects: updatedDefects
                }
            });
        }

        case ActionTypes.ASSIGN_IMAGES_TO_DEFECT_FAILED: {
            // Handle error state if needed
            console.error("Failed to assign images to defect:", action.errorMessage);
            return state;
        }

        case ActionTypes.SET_EXPIRY_DATE_ERROR: {
            const inspection = state.activeInspection;


            const newSections = updateSection(
                {
                    expiryDateError: true
                },
                inspection?.sections || [],
                action.section.toString()
            )

            return updateObject(state, {
                activeInspection: {
                    ...inspection,
                    sections: newSections
                }
            });
        }

        case ActionTypes.SET_SAVING_EXPIRY_DATE: {
            const inspection = state.activeInspection;

            const newSections = updateSection(
                {
                    expiryDateError: false,
                    savingExpiryDate: true
                },
                inspection?.sections || [],
                action.section.toString()
            )

            return updateObject(state, {
                activeInspection: {
                    ...inspection,
                    sections: newSections
                }
            });
        }

        case ActionTypes.UPDATE_GLOBAL_EXPIRY_DATE: {
            const inspection = state.activeInspection;

            if (!inspection || !action.globalExpiryDate) {
                return state;
            }

            const epochTime = new Date(action.globalExpiryDate).getTime();

            inspection.sections.forEach((section: InspectionElement) => {
                if(section.ReUseDocuments){
                    section.expiryDate = epochTime
                }
            })

            return updateObject(state, {
                globalExpiryDate: action.globalExpiryDate?.toString(),
                activeInspection: {...inspection}
            });
        }

        case ActionTypes.SAVING_EXPIRY_DATE_SUCCESS: {
            const inspection = state.activeInspection;

            const newSections = updateSection(
                {
                    expiryDateError: false,
                    savingExpiryDate: false,
                    expiryDate: action.newExpiryDate
                },
                inspection?.sections || [],
                action.section.toString()
            )

            return updateObject(state, {
                activeInspection: {
                    ...inspection,
                    sections: newSections
                }
            });
        }
    }
    return state;
};

export default portalReducer;
